import { Injectable, signal } from "@angular/core";

import { InputSelectOption } from "@components/shared/inputs/input-select/input-select.model";

import { RelatedRiskAssessmentReportsTableModel as Model, RiskAssessmentReport } from "../models";

const {
  ALL_RISK_LEVELS,
  NONE_RISK_LEVEL,
  NOT_SELECTED_TAG_CLASS,
  REVERSE_ORDER_CLASS,
  CHECKED_ICON,
} = Model;

const order = {
  [ALL_RISK_LEVELS.value]: -2,
  [NONE_RISK_LEVEL.value]: -1,
};

@Injectable({ providedIn: "root" })
export class RiskAssessmentReportsRiskLevelTagsService {
  private tagsMap = new Map<string, Model.ResidualRiskTag>();

  init(allCount = 0) {
    this.tagsMap.clear();

    this.tagsMap.set(ALL_RISK_LEVELS.value, {
      label: ALL_RISK_LEVELS.label,
      value: ALL_RISK_LEVELS.value,
      icon: signal(CHECKED_ICON),
      count: allCount,
      classes: signal([REVERSE_ORDER_CLASS]),
    });
  }

  get(): InputSelectOption[] {
    return Array.from(this.tagsMap.values(), ({ value, label, classes, count, icon }) => ({
      value,
      label: `${label} (${count})`,
      class: [...classes(), "no-underline"].join(" "),
      icon: icon(),
    }));
  }

  add(report: RiskAssessmentReport) {
    if (!report.residualRisk) {
      if (this.tagsMap.get(NONE_RISK_LEVEL.value)) {
        this.tagsMap.get(NONE_RISK_LEVEL.value).count++;
      } else {
        this.tagsMap.set(NONE_RISK_LEVEL.value, {
          label: NONE_RISK_LEVEL.label,
          value: NONE_RISK_LEVEL.value,
          icon: signal(null),
          count: 1,
          classes: signal([NOT_SELECTED_TAG_CLASS, REVERSE_ORDER_CLASS]),
        });

        this.sort();
      }
    } else {
      const { title, color } = report.residualRisk;
      const id = this.getId(report.residualRisk);

      if (this.tagsMap.has(id)) {
        this.tagsMap.get(id).count++;
      } else {
        const isAllSelected = this.tagsMap.get(ALL_RISK_LEVELS.value).icon() === CHECKED_ICON;

        this.tagsMap.set(id, {
          label: title,
          value: id,
          classes: signal([color, NOT_SELECTED_TAG_CLASS, REVERSE_ORDER_CLASS]),
          icon: signal(isAllSelected ? CHECKED_ICON : null),
          count: 1,
        });

        this.sort();
      }
    }

    this.tagsMap.get(ALL_RISK_LEVELS.value).count++;
  }

  update(tagValue: string) {
    this.tagsMap.forEach(({ value, icon, classes }) => {
      const isSelected = value === tagValue;
      const isAllSelected = ALL_RISK_LEVELS.value === tagValue;

      icon.set(isSelected || isAllSelected ? CHECKED_ICON : null);

      const classNames = classes().filter((c) => c !== NOT_SELECTED_TAG_CLASS);

      if (!isSelected) {
        classNames.push(NOT_SELECTED_TAG_CLASS);
      }

      classes.set(classNames);
    });
  }

  remove(report: RiskAssessmentReport) {
    if (!report.residualRisk) {
      this.decreaseCount(NONE_RISK_LEVEL.value);
    } else {
      const id = this.getId(report.residualRisk);

      this.decreaseCount(id);
    }

    this.tagsMap.get(ALL_RISK_LEVELS.value).count--;
  }

  getId(risk: RiskAssessmentReport["residualRisk"]) {
    const { title, color } = risk;

    return `${title}-${color}`;
  }

  exists(risk: RiskAssessmentReport["residualRisk"]) {
    if (!risk) {
      return this.tagsMap.has(NONE_RISK_LEVEL.value);
    }

    const id = this.getId(risk);

    return this.tagsMap.has(id);
  }

  private sort() {
    const sortedTags = Array.from(this.tagsMap).sort(
      ([a], [b]) => (order[a] ?? 0) - (order[b] ?? 0) || a.localeCompare(b),
    );

    this.tagsMap = new Map(sortedTags);
  }

  private decreaseCount(id: string) {
    if (this.tagsMap.get(id)) {
      const tag = this.tagsMap.get(id)!;

      tag.count--;

      if (tag.count === 0) {
        this.tagsMap.delete(id);
      }
    }
  }
}
